import classNames from 'classnames';
import type { PropsWithChildren } from 'react';
import React, { forwardRef, use, useImperativeHandle, useReducer, useRef } from 'react';

import { SiteContext } from '../../context/site-context';
import imageFade from '../../helpers/image-fades';

import * as styles from './faded-image.module.scss';
import FixedImage from './fixed-image';
import HostedImage, { type HostedImageDetails } from './hosted-image';
import ImageLoadingSkeleton from './image-loading-skeleton';

export enum RoundedCornersOptions {
  YES,
  THEME_DEFAULT,
  NO,
}

export enum FadeInEffectOption {
  NONE,
  IMMEDIATELY, // do not wait for the image to load or for React to hydrate
  AFTER_LOAD,
}

export type FadedImageProps = PropsWithChildren<{
  contentWrapperClassName?: string;
  className?: string;
  image: Queries.ImageDetails | Queries.ImageWithCropDetails | undefined | null;
  enablePreload?: boolean;
  fadeType?: string;
  mobileFadeType?: string;
  shadow?: boolean;
  gtmTrackingId?: string;
  roundedCorners?: RoundedCornersOptions;
  zoomOnHover?: boolean;
  isDownloadApp?: boolean;
  loading?: 'eager' | 'lazy';
  loadingSkeleton?: boolean;
  fadeInEffect?: FadeInEffectOption;
}> &
  (
    | {
        fixedImage: true;
        imageWidthLimit?: undefined;
        enableBlurredPlaceholder?: undefined;
      }
    | {
        fixedImage?: false;
        imageWidthLimit?: number;
        enableBlurredPlaceholder?: boolean;
      }
  );

export const BLURRED_PLACEHOLDER_IMAGE_WIDTH = 200;

export const getHostedImageDetails = (image: NonNullable<FadedImageProps['image']>) =>
  ({
    ...image,
    ...(image as Queries.ImageWithCropDetails)?.cropDetails,
  }) as HostedImageDetails;

const FadedImage = forwardRef<HTMLImageElement, FadedImageProps>(
  (
    {
      contentWrapperClassName,
      className,
      image,
      enablePreload = false,
      enableBlurredPlaceholder = false,
      children,
      fadeType,
      mobileFadeType,
      shadow,
      gtmTrackingId,
      roundedCorners = RoundedCornersOptions.THEME_DEFAULT,
      fixedImage,
      zoomOnHover,
      isDownloadApp,
      loading,
      loadingSkeleton = false,
      fadeInEffect = FadeInEffectOption.NONE,
      imageWidthLimit,
      ...restProps
    },
    ref
  ) => {
    const [isImageLoaded, setImageIsLoaded] = useReducer(() => true, false);
    const innerRef = useRef(null);
    const { isMeridian } = use(SiteContext);
    useImperativeHandle(ref, () => innerRef.current!, []);

    if (!image) return null;

    const imageDetails = getHostedImageDetails(image);

    const fadeInClassName = (() => {
      switch (fadeInEffect) {
        case FadeInEffectOption.NONE:
          return undefined;
        case FadeInEffectOption.IMMEDIATELY:
          return styles.fadeInImmediately;
        case FadeInEffectOption.AFTER_LOAD:
          return classNames(styles.fadeInAfterLoad, !isImageLoaded && styles.imageNotLoaded);
      }
    })();

    const hasRoundedCorners = getShouldUseRoundedCorners(
      roundedCorners,
      !!image?.leftHighlight,
      isMeridian
    );

    return (
      <div
        className={classNames(
          styles.wrapper,
          hasRoundedCorners && styles.roundedCorners,
          isDownloadApp && styles.downloadApp,
          !!image?.leftHighlight && styles.leftHighlight,
          zoomOnHover && styles.zoomOnHover,
          fadeInClassName,
          shadow && styles.shadow,
          className
        )}
        data-gtmid={gtmTrackingId || className}
        {...restProps}
      >
        {fixedImage ? (
          <FixedImage
            imageDetails={imageDetails}
            ref={innerRef}
            loading={loading}
            onImageLoaded={() => {
              setImageIsLoaded();
            }}
            enablePreload={enablePreload}
          />
        ) : (
          <>
            {enableBlurredPlaceholder && (
              <HostedImage
                imageDetails={imageDetails}
                imageWidthLimit={BLURRED_PLACEHOLDER_IMAGE_WIDTH}
                enableInlineImages
              />
            )}
            <HostedImage
              imageDetails={imageDetails}
              ref={innerRef}
              loading={loading}
              onImageLoaded={() => {
                setImageIsLoaded();
              }}
              enablePreload={enablePreload}
              imageWidthLimit={imageWidthLimit}
              className={classNames(
                enableBlurredPlaceholder && styles.blurredPlaceholderImage,
                enableBlurredPlaceholder && !isImageLoaded && styles.imageHidden
              )}
            />
          </>
        )}
        {loadingSkeleton ? <ImageLoadingSkeleton imageRef={innerRef} /> : null}
        <div
          className={classNames(
            styles.contentWrapper,
            enableBlurredPlaceholder && styles.blurredPlaceholder,
            enableBlurredPlaceholder && isImageLoaded && styles.imageLoaded,
            contentWrapperClassName
          )}
          style={
            {
              '--faded-image-gradient-mobile': mobileFadeType
                ? `linear-gradient(${mobileFadeType})`
                : `linear-gradient(${fadeType || imageFade.none})`,
              '--faded-image-gradient-desktop': `linear-gradient(${fadeType || imageFade.none})`,
            } as React.CSSProperties
          }
        >
          {children}
        </div>
      </div>
    );
  }
);

const getShouldUseRoundedCorners = (
  roundedCornersOption: RoundedCornersOptions,
  isLeftHighlight: boolean,
  isMeridian: boolean
) => {
  if (roundedCornersOption === RoundedCornersOptions.NO) {
    return false;
  }
  if (isMeridian) {
    return true;
  }
  return roundedCornersOption === RoundedCornersOptions.YES && !isLeftHighlight;
};

FadedImage.displayName = 'FadedImage';

export default FadedImage;
